import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl, ActivityIndicator, Platform, useWindowDimensions } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { AtmosphericStage, StageCard, JuicyButton, CandyIconButton } from '../components/primitives';
import { KeyStage, YearGroup } from '@eduwrite/shared';
import { Calculator, BookOpen, FlaskConical, Globe, Compass, Laptop, Sparkles, ChevronLeft } from 'lucide-react-native';
import { useDeviceStore } from '../store/useDeviceStore';
import { FreestyleService } from '../services/FreestyleService';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

type Props = StackScreenProps<RootStackParamList, 'FreestyleCurriculum'>;

interface SubjectOption {
    id: string;
    label: string;
    icon: React.ComponentType<{ size: number; color: string }>;
    accentColor: string;
    bgColor: string;
}

const SUBJECTS: SubjectOption[] = [
    { id: 'maths', label: 'Maths', icon: Calculator, accentColor: '#2563EB', bgColor: '#EFF6FF' },
    { id: 'english', label: 'English', icon: BookOpen, accentColor: '#D97706', bgColor: '#FFFBEB' },
    { id: 'science', label: 'Science', icon: FlaskConical, accentColor: '#059669', bgColor: '#ECFDF5' },
    { id: 'geography', label: 'Geography', icon: Globe, accentColor: '#0D9488', bgColor: '#F0FDFA' },
    { id: 'history', label: 'History', icon: Compass, accentColor: '#7C3AED', bgColor: '#F5F3FF' },
    { id: 'computing', label: 'Computing', icon: Laptop, accentColor: '#4F46E5', bgColor: '#EEF2FF' },
];

const MATH_STRANDS: Record<string, Array<{ id: string; label: string }>> = {
    EYFS: [
        { id: 'all', label: '⭐ Quick Mix' },
        { id: 'number_place_value', label: 'Counting & Bonds' },
        { id: 'arithmetic', label: 'Simple Addition' },
    ],
    KS1: [
        { id: 'all', label: '⭐ Quick Mix' },
        { id: 'number_place_value', label: 'Number Bonds to 10 & 20' },
        { id: 'arithmetic', label: 'Addition & Subtraction' },
        { id: 'times_tables', label: '2, 5 & 10 Times Tables' },
    ],
    KS2: [
        { id: 'all', label: '⭐ Quick Mix' },
        { id: 'times_tables', label: 'Times Tables Challenge (MTC)' },
        { id: 'arithmetic', label: 'Mental Arithmetic' },
        { id: 'number_place_value', label: 'Number & Place Value' },
    ],
};

export const FreestyleCurriculumScreen: React.FC<Props> = ({ navigation }) => {
    const { width } = useWindowDimensions();
    const isMobile = width < 600;
    const { serverUrl } = useDeviceStore();
    const { isOnline } = useNetworkStatus();
    const [keyStage, setKeyStage] = useState<KeyStage>('KS1');
    const [yearGroup, setYearGroup] = useState<YearGroup>('y2');
    const [selectedSubject, setSelectedSubject] = useState<string>('maths');
    const [selectedStrand, setSelectedStrand] = useState<string>('all');
    const [hasScrolled, setHasScrolled] = useState<boolean>(false);
    const [availableSubjects, setAvailableSubjects] = useState<string[]>(() => {
        return FreestyleService.getCachedSubjects('y2') || SUBJECTS.map(s => s.id);
    });
    const [isCheckingAvailability, setIsCheckingAvailability] = useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [isOfflineNotice, setIsOfflineNotice] = useState<boolean>(false);

    const checkAvailability = useCallback(async (yg: YearGroup, force = false) => {
        // If we don't already have valid cached subjects for this year group, don't prematurely
        // grey out cards while network check is in flight.
        const cached = FreestyleService.getCachedSubjects(yg);
        if (cached) {
            setAvailableSubjects(cached);
        }

        setIsCheckingAvailability(true);
        try {
            const res = await FreestyleService.fetchAvailableSubjects(yg, {
                forceRefresh: force,
                customServerUrl: serverUrl || undefined,
            });
            setAvailableSubjects(res.subjects);
            setIsOfflineNotice(res.isOffline);

            // Once availability has been verified, only pivot selectedSubject if the currently
            // selected subject is genuinely not available.
            setSelectedSubject(prev => {
                if (res.subjects.length > 0 && !res.subjects.includes(prev)) {
                    return res.subjects[0] || 'maths';
                }
                return prev;
            });
        } catch (err) {
            console.warn('[FreestyleCurriculum] Failed to check available subjects:', err);
        } finally {
            setIsCheckingAvailability(false);
            setIsRefreshing(false);
        }
    }, [serverUrl]);

    // Check availability whenever yearGroup or serverUrl changes
    useEffect(() => {
        checkAvailability(yearGroup);
    }, [yearGroup, checkAvailability]);

    // When connectivity comes back online, automatically revalidate and refresh
    useEffect(() => {
        if (isOnline) {
            FreestyleService.invalidateCache(yearGroup);
            checkAvailability(yearGroup, true);
        }
    }, [isOnline, yearGroup, checkAvailability]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        FreestyleService.invalidateCache(yearGroup);
        checkAvailability(yearGroup, true);
    };

    const handleKeyStageChange = (ks: KeyStage) => {
        setKeyStage(ks);
        if (ks === 'EYFS') setYearGroup('reception');
        else if (ks === 'KS1') setYearGroup('y2');
        else setYearGroup('y4');
        setSelectedStrand('all');
    };

    const handleStart = () => {
        const subjectDef = SUBJECTS.find(s => s.id === selectedSubject);
        navigation.navigate('FreestyleActive', {
            keyStage,
            yearGroup,
            subject: selectedSubject,
            strand: selectedStrand === 'all' ? undefined : selectedStrand,
            subjectLabel: subjectDef?.label || 'Maths',
        });
    };

    const currentMathStrands = MATH_STRANDS[keyStage] || MATH_STRANDS.KS1;

    return (
        <AtmosphericStage>
            {/* Top Navigation */}
            <View style={styles.topBar}>
                <CandyIconButton
                    icon={ChevronLeft}
                    onPress={() => navigation.goBack()}
                    accessibilityLabel="Go back to home"
                    accessibilityHint="Returns to the main home screen"
                />
                <View style={styles.headerBadge}>
                    <Sparkles size={20} color="#F59E0B" />
                    <Text style={styles.headerBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        Free Learning Mode
                    </Text>
                </View>
                <View style={{ width: 48 }} />
            </View>

            <ScrollView
                style={styles.scrollArea}
                contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}
                showsVerticalScrollIndicator={true}
                bounces={true}
                onScroll={() => {
                    if (!hasScrolled) setHasScrolled(true);
                }}
                scrollEventThrottle={32}
                keyboardShouldPersistTaps="handled"
                refreshControl={
                    Platform.OS !== 'web' ? (
                        <RefreshControl
                            refreshing={isRefreshing}
                            onRefresh={handleRefresh}
                            colors={['#4F46E5']}
                            tintColor="#4F46E5"
                        />
                    ) : undefined
                }
            >
                <StageCard style={styles.stageCard}>
                    {/* Section 1: Choose Key Stage */}
                    <Text style={styles.stepTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        1. Choose Your Key Stage
                    </Text>
                    <View style={styles.keyStageTabs}>
                        {(['EYFS', 'KS1', 'KS2'] as KeyStage[]).map((ks) => {
                            const active = keyStage === ks;
                            return (
                                <TouchableOpacity
                                    key={ks}
                                    style={[styles.ksTab, active && styles.ksTabActive]}
                                    onPress={() => handleKeyStageChange(ks)}
                                    activeOpacity={0.8}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: active }}
                                    accessibilityLabel={`Key Stage ${ks}`}
                                >
                                    <Text style={[styles.ksTabText, active && styles.ksTabTextActive]} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                                        {ks}
                                    </Text>
                                    <Text style={[styles.ksSubText, active && styles.ksSubTextActive]} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                                        {ks === 'EYFS' ? 'Ages 4–5' : ks === 'KS1' ? 'Ages 5–7' : 'Ages 7–11'}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>

                    {/* Section 2: Choose Year Group */}
                    <Text style={styles.stepTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        2. Choose Your Year Group
                    </Text>
                    <View style={styles.yearGroupRow}>
                        {keyStage === 'EYFS' && (
                            <TouchableOpacity
                                style={[styles.ygPill, yearGroup === 'reception' && styles.ygPillActive]}
                                onPress={() => setYearGroup('reception')}
                                activeOpacity={0.8}
                                accessibilityRole="button"
                                accessibilityState={{ selected: yearGroup === 'reception' }}
                            >
                                <Text style={[styles.ygPillText, yearGroup === 'reception' && styles.ygPillTextActive]}>
                                    Reception (EYFS)
                                </Text>
                            </TouchableOpacity>
                        )}
                        {keyStage === 'KS1' && (
                            <>
                                <TouchableOpacity
                                    style={[styles.ygPill, yearGroup === 'y1' && styles.ygPillActive]}
                                    onPress={() => setYearGroup('y1')}
                                    activeOpacity={0.8}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: yearGroup === 'y1' }}
                                >
                                    <Text style={[styles.ygPillText, yearGroup === 'y1' && styles.ygPillTextActive]}>
                                        Year 1
                                    </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={[styles.ygPill, yearGroup === 'y2' && styles.ygPillActive]}
                                    onPress={() => setYearGroup('y2')}
                                    activeOpacity={0.8}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: yearGroup === 'y2' }}
                                >
                                    <Text style={[styles.ygPillText, yearGroup === 'y2' && styles.ygPillTextActive]}>
                                        Year 2
                                    </Text>
                                </TouchableOpacity>
                            </>
                        )}
                        {keyStage === 'KS2' && (
                            <>
                                {(['y3', 'y4', 'y5', 'y6'] as YearGroup[]).map((yg) => {
                                    const active = yearGroup === yg;
                                    const yNum = yg.replace('y', '');
                                    return (
                                        <TouchableOpacity
                                            key={yg}
                                            style={[styles.ygPill, active && styles.ygPillActive]}
                                            onPress={() => setYearGroup(yg)}
                                            activeOpacity={0.8}
                                            accessibilityRole="button"
                                            accessibilityState={{ selected: active }}
                                        >
                                            <Text style={[styles.ygPillText, active && styles.ygPillTextActive]}>
                                                Year {yNum}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </>
                        )}
                    </View>

                    {/* Section 3: Choose Subject */}
                    <View style={styles.stepTitleRow}>
                        <Text style={styles.stepTitle} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                            3. Pick a Subject
                        </Text>
                        {isCheckingAvailability && (
                            <ActivityIndicator size="small" color="#6366F1" style={styles.loadingSpinner} />
                        )}
                        {isOfflineNotice && (
                            <View style={styles.offlinePill}>
                                <Text style={styles.offlinePillText} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                                    Offline Mode
                                </Text>
                            </View>
                        )}
                    </View>
                    <View style={styles.subjectGrid}>
                        {SUBJECTS.map((subj) => {
                            const isAvailable = availableSubjects.includes(subj.id);
                            const active = selectedSubject === subj.id && isAvailable;
                            const IconComponent = subj.icon;
                            return (
                                <TouchableOpacity
                                    key={subj.id}
                                    disabled={!isAvailable}
                                    style={[
                                        styles.subjectCard,
                                        {
                                            backgroundColor: isAvailable ? subj.bgColor : '#F1F5F9',
                                            borderColor: active ? subj.accentColor : '#E2E8F0',
                                            opacity: isAvailable ? 1 : 0.45,
                                        },
                                        active && styles.subjectCardActive,
                                    ]}
                                    onPress={() => {
                                        if (!isAvailable) return;
                                        setSelectedSubject(subj.id);
                                        setSelectedStrand('all');
                                    }}
                                    activeOpacity={0.8}
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: active, disabled: !isAvailable }}
                                    accessibilityLabel={isAvailable ? subj.label : `${subj.label} (Coming Soon)`}
                                >
                                    {!isAvailable && (
                                        <View style={styles.comingSoonBadge}>
                                            <Text style={styles.comingSoonBadgeText} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                                                Coming Soon
                                            </Text>
                                        </View>
                                    )}
                                    <View style={[styles.subjectIconWrap, isMobile && styles.subjectIconWrapMobile, { backgroundColor: isAvailable ? subj.accentColor : '#94A3B8' }]}>
                                        <IconComponent size={isMobile ? 20 : 24} color="#FFFFFF" />
                                    </View>
                                    <Text
                                        style={[
                                            styles.subjectLabel,
                                            isMobile && styles.subjectLabelMobile,
                                            active && { color: subj.accentColor, fontWeight: '900' },
                                            !isAvailable && { color: '#64748B' },
                                        ]}
                                    >
                                        {subj.label}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>

                    {/* Section 4: If Maths, Strand/Topic Pills */}
                    {selectedSubject === 'maths' && (
                        <>
                            <Text style={[styles.stepTitle, isMobile && styles.stepTitleMobile]} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                4. Focus Topic
                            </Text>
                            <View style={styles.strandWrap}>
                                {currentMathStrands.map((st) => {
                                    const active = selectedStrand === st.id;
                                    return (
                                        <TouchableOpacity
                                            key={st.id}
                                            style={[styles.strandPill, isMobile && styles.strandPillMobile, active && styles.strandPillActive]}
                                            onPress={() => setSelectedStrand(st.id)}
                                            activeOpacity={0.8}
                                            accessibilityRole="button"
                                            accessibilityState={{ selected: active }}
                                        >
                                            <Text style={[styles.strandText, isMobile && styles.strandTextMobile, active && styles.strandTextActive]}>
                                                {st.label}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </>
                    )}

                    {/* Action Button */}
                    <View style={[styles.actionContainer, isMobile && styles.actionContainerMobile]}>
                        <JuicyButton
                            label="LET'S GO! 🚀"
                            variant="primary"
                            size={isMobile ? 'medium' : 'large'}
                            disabled={!availableSubjects.includes(selectedSubject)}
                            onPress={handleStart}
                            testID="start-freestyle-button"
                        />
                    </View>

                    {/* Mobile scroll hint */}
                    {isMobile && !hasScrolled && (
                        <View style={styles.scrollCueWrap}>
                            <Text style={styles.scrollCueText}>↓ Scroll to choose topics &amp; start ↓</Text>
                        </View>
                    )}
                </StageCard>
            </ScrollView>
        </AtmosphericStage>
    );
};

const styles = StyleSheet.create({
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 6,
        zIndex: 10,
    },
    headerBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        paddingHorizontal: 14,
        paddingVertical: 6,
        borderRadius: 20,
        gap: 6,
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 2,
    },
    headerBadgeText: {
        fontSize: 15,
        fontWeight: '800',
        color: '#1E293B',
    },
    scrollArea: {
        flex: 1,
    },
    scrollContent: {
        paddingHorizontal: 16,
        paddingBottom: 28,
        alignItems: 'center',
    },
    scrollContentMobile: {
        paddingHorizontal: 10,
        paddingBottom: 20,
    },
    stageCard: {
        width: '100%',
        maxWidth: 720,
        marginTop: 4,
    },
    stepTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        marginBottom: 8,
        gap: 8,
    },
    stepTitle: {
        fontSize: 16,
        fontWeight: '900',
        color: '#1E293B',
        marginTop: 12,
        marginBottom: 8,
    },
    stepTitleMobile: {
        fontSize: 15,
        marginTop: 10,
        marginBottom: 6,
    },
    loadingSpinner: {
        marginLeft: 4,
    },
    offlinePill: {
        backgroundColor: '#FEF3C7',
        borderColor: '#F59E0B',
        borderWidth: 1,
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    offlinePillText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#92400E',
    },
    keyStageTabs: {
        flexDirection: 'row',
        gap: 8,
    },
    ksTab: {
        flex: 1,
        backgroundColor: '#F1F5F9',
        borderRadius: 16,
        paddingVertical: 10,
        alignItems: 'center',
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderBottomWidth: 3.5,
    },
    ksTabActive: {
        backgroundColor: '#EFF6FF',
        borderColor: '#3B82F6',
        borderBottomColor: '#1D4ED8',
    },
    ksTabText: {
        fontSize: 17,
        fontWeight: '900',
        color: '#64748B',
    },
    ksTabTextActive: {
        color: '#1D4ED8',
    },
    ksSubText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#94A3B8',
        marginTop: 2,
    },
    ksSubTextActive: {
        color: '#3B82F6',
    },
    yearGroupRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    ygPill: {
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderBottomWidth: 3,
        borderRadius: 18,
        paddingVertical: 8,
        paddingHorizontal: 14,
        minHeight: 40,
        justifyContent: 'center',
        alignItems: 'center',
    },
    ygPillActive: {
        backgroundColor: '#ECFDF5',
        borderColor: '#10B981',
        borderBottomColor: '#059669',
    },
    ygPillText: {
        fontSize: 14,
        fontWeight: '800',
        color: '#475569',
    },
    ygPillTextActive: {
        color: '#065F46',
    },
    subjectGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    subjectCard: {
        flexBasis: '46%',
        flexGrow: 1,
        minHeight: 68,
        borderRadius: 18,
        borderWidth: 2,
        borderBottomWidth: 3.5,
        padding: 8,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        position: 'relative',
    },
    comingSoonBadge: {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: '#E2E8F0',
        paddingHorizontal: 5,
        paddingVertical: 1,
        borderRadius: 6,
    },
    comingSoonBadgeText: {
        fontSize: 9,
        fontWeight: '800',
        color: '#64748B',
    },
    subjectCardActive: {
        transform: [{ scale: 1.02 }],
    },
    subjectIconWrap: {
        width: 38,
        height: 38,
        borderRadius: 19,
        alignItems: 'center',
        justifyContent: 'center',
    },
    subjectIconWrapMobile: {
        width: 34,
        height: 34,
        borderRadius: 17,
    },
    subjectLabel: {
        fontSize: 13,
        fontWeight: '800',
        color: '#334155',
    },
    subjectLabelMobile: {
        fontSize: 12,
    },
    strandWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    strandPill: {
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderRadius: 14,
        paddingVertical: 6,
        paddingHorizontal: 12,
    },
    strandPillMobile: {
        paddingVertical: 5,
        paddingHorizontal: 10,
    },
    strandPillActive: {
        backgroundColor: '#FEF3C7',
        borderColor: '#F59E0B',
    },
    strandText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#64748B',
    },
    strandTextMobile: {
        fontSize: 12,
    },
    strandTextActive: {
        color: '#92400E',
        fontWeight: '800',
    },
    actionContainer: {
        marginTop: 20,
        alignItems: 'center',
        width: '100%',
    },
    actionContainerMobile: {
        marginTop: 14,
    },
    scrollCueWrap: {
        marginTop: 12,
        paddingVertical: 6,
        paddingHorizontal: 14,
        backgroundColor: '#EFF6FF',
        borderRadius: 12,
        alignSelf: 'center',
        borderWidth: 1,
        borderColor: '#BFDBFE',
    },
    scrollCueText: {
        fontSize: 11,
        fontWeight: '700',
        color: '#2563EB',
    },
});
